import { useCallback, useEffect, useRef, useState } from 'react';

import type { AssetLoadCallback, Event, Layout, RiveFile } from '@rive-app/react-canvas';
import { EventType, useRive, useStateMachineInput } from '@rive-app/react-canvas';
import noop from 'lodash/noop';
import { reaction } from 'mobx';

import { logToNewRelic } from '../../services/debug';
import { useStores } from '../../stores/useStores';
import { LOADING_TIMED_OUT_MESSAGE, useLoadingTimeout } from '../useLoadingTimeout';

import { RIVE_DEFAULTS } from './constants';
import './initRiveRuntime';
import { diagnoseRiveContents } from './riveContentsDiagnostics';
import { logRiveLoad, riveLoadInstrumentation } from './riveLoadInstrumentation';

interface UseRiveMachineProps {
  src?: string;
  stateMachineName?: string;
  autoplay?: boolean;
  autoBind?: boolean;
  layout?: Layout;
  artboard?: string;
  onLoad?: () => void;
  onLoadError?: (error: any) => void;
  loadingTimeout?: number;
  onStateChange?: (event: Event) => void;
  onRiveEvent?: (event: Event) => void;
  assetLoader?: AssetLoadCallback;
}

export const useRiveMachine = ({
  src,
  stateMachineName = RIVE_DEFAULTS.STATE_MACHINE_NAME,
  autoplay = true,
  autoBind = false,
  artboard,
  onLoad,
  onLoadError,
  loadingTimeout = RIVE_DEFAULTS.TIMEOUT,
  layout = RIVE_DEFAULTS.LAYOUT,
  onStateChange,
  onRiveEvent,
  assetLoader,
}: UseRiveMachineProps) => {
  const { riveFileStore } = useStores();
  const [error, setError] = useState<Event | null>(null);
  const [hasRiveLoaded, setHasRiveLoaded] = useState<boolean>(false);
  const [riveFile, setRiveFile] = useState<RiveFile | undefined>(() => (src ? riveFileStore.getFile(src) : undefined));

  const startedAtRef = useRef<number>(0);
  const reportedContentsRef = useRef<string | null>(null);
  const reportedMissesRef = useRef<Set<string>>(new Set());
  const mountInfoRef = useRef<{ active: number; mountIndex: number } | null>(null);
  const loggedRef = useRef<boolean>(false);
  const cacheHitAtMountRef = useRef<boolean>(false);

  // Subscribe to the MobX cache so consumers that aren't wrapped in
  // observer() still re-render when the preloader fills the file in.
  // Also ref-counts the entry so the store can release the underlying
  // RiveFile (and free WASM memory) once the last consumer unmounts.
  useEffect(() => {
    if (!src) {
      setRiveFile(undefined);
      return;
    }

    const initial = riveFileStore.acquire(src);
    setRiveFile(initial);

    if (!initial) {
      riveFileStore.markLoading(src);
    }

    startedAtRef.current = performance.now();
    loggedRef.current = false;
    cacheHitAtMountRef.current = Boolean(initial);
    mountInfoRef.current = riveLoadInstrumentation.incActive();

    const dispose = reaction(
      () => riveFileStore.getFile(src),
      (file) => setRiveFile(file),
    );

    return () => {
      dispose();
      riveFileStore.release(src);
      riveFileStore.clearLoading(src);
      riveLoadInstrumentation.decActive();
      // Emit an abandoned-load row if the canvas never finished loading.
      if (!loggedRef.current && startedAtRef.current > 0) {
        loggedRef.current = true;
        logRiveLoad({
          src,
          durationMs: Math.round(performance.now() - startedAtRef.current),
          activeRiveCount: mountInfoRef.current?.active ?? 0,
          mountIndex: mountInfoRef.current?.mountIndex ?? 0,
          cacheHit: cacheHitAtMountRef.current,
          isPreload: false,
          outcome: 'abandoned',
          timeoutMs: loadingTimeout,
        });
      }
    };
  }, [riveFileStore, src, loadingTimeout]);

  const handleLoad = useCallback(() => {
    setHasRiveLoaded(true);
    onLoad?.();
    if (loggedRef.current) return;
    loggedRef.current = true;
    logRiveLoad({
      src,
      durationMs: Math.round(performance.now() - startedAtRef.current),
      activeRiveCount: mountInfoRef.current?.active ?? riveLoadInstrumentation.getActive(),
      mountIndex: mountInfoRef.current?.mountIndex ?? 0,
      cacheHit: cacheHitAtMountRef.current,
      isPreload: false,
      outcome: 'success',
      timeoutMs: loadingTimeout,
    });
  }, [onLoad, src, loadingTimeout]);

  const handleLoadError = useCallback(
    (loadError: any) => {
      onLoadError?.(loadError);
      setError(loadError);
      logToNewRelic('Rive Load Error', { message: loadError, src }, 'error');
      if (loggedRef.current) return;
      loggedRef.current = true;
      const isTimeout = loadError === LOADING_TIMED_OUT_MESSAGE;
      logRiveLoad({
        src,
        durationMs: Math.round(performance.now() - startedAtRef.current),
        activeRiveCount: mountInfoRef.current?.active ?? riveLoadInstrumentation.getActive(),
        mountIndex: mountInfoRef.current?.mountIndex ?? 0,
        cacheHit: cacheHitAtMountRef.current,
        isPreload: false,
        outcome: isTimeout ? 'timeout' : 'error',
        errorMessage: typeof loadError === 'string' ? loadError : String(loadError),
        timeoutMs: loadingTimeout,
      });
    },
    [onLoadError, src, loadingTimeout],
  );

  const { rive, RiveComponent } = useRive({
    src,
    riveFile,
    stateMachines: stateMachineName,
    artboard,
    autoplay,
    autoBind,
    layout,
    assetLoader,
    onLoad: handleLoad,
    onLoadError: handleLoadError,
    onStateChange,
  });

  useEffect(() => {
    if (!rive || !onRiveEvent) return;

    rive.on(EventType.RiveEvent, onRiveEvent);

    return () => {
      rive.off(EventType.RiveEvent, onRiveEvent);
    };
  }, [rive, onRiveEvent]);

  useEffect(() => {
    setHasRiveLoaded(false);
    setError(null);
    reportedContentsRef.current = null;
    reportedMissesRef.current.clear();
  }, [src]);

  // A .riv that loads fine can still render nothing, and Rive reports none of it: a requested
  // artboard or state machine that the file doesn't contain is ignored, and autoBind leaves
  // `viewModelInstance` null rather than throwing. Both look identical to a broken file, so
  // compare what was asked for against what the file actually holds and say so once per src.
  useEffect(() => {
    if (!rive || !hasRiveLoaded || reportedContentsRef.current === src) return;

    const { problems, fileArtboards, fileStateMachines } = diagnoseRiveContents({
      contents: rive.contents,
      artboard,
      stateMachineName,
      autoBind,
      hasViewModelInstance: Boolean(rive.viewModelInstance),
    });
    if (problems.length === 0) return;

    reportedContentsRef.current = src ?? null;
    const detail = {
      message: problems.join('; '),
      src,
      requestedArtboard: artboard,
      requestedStateMachine: stateMachineName,
      fileArtboards,
      fileStateMachines,
    };
    console.error('[rive] file does not match what was requested', detail);
    logToNewRelic('Rive Contents Mismatch', detail, 'error');
  }, [rive, hasRiveLoaded, src, artboard, stateMachineName, autoBind]);

  const isLoaded = hasRiveLoaded || Boolean(riveFile) || Boolean(rive?.isPlaying);

  const { isLoading: isTimeoutLoading, error: timeoutError } = useLoadingTimeout({
    resource: src,
    hasLoaded: isLoaded,
    timeoutEnabled: Boolean(src) && !riveFile,
    timeout: loadingTimeout,
    onTimeout: handleLoadError,
  });

  // Every helper below used to fail by returning false/noop with no trace, which made a dead
  // binding indistinguishable from a working one. These run on each state push, so report only
  // the first occurrence of each distinct miss rather than flooding on every render.
  const reportViewModelMiss = useCallback(
    (path: string, reason: string) => {
      const key = `${reason}:${path}`;
      if (reportedMissesRef.current.has(key)) return;
      reportedMissesRef.current.add(key);

      const detail = { message: reason, path, src };
      console.error('[rive] view model binding failed', detail);
      logToNewRelic('Rive View Model Miss', detail, 'error');
    },
    [src],
  );

  const fireRiveViewModelTrigger = useCallback(
    (triggerPath: string): boolean => {
      if (!rive) return false;

      const vm = rive.viewModelInstance;

      if (!vm) {
        reportViewModelMiss(triggerPath, 'no view model instance bound');
        return false;
      }

      const trigger = vm.trigger?.(triggerPath);

      if (!trigger) {
        reportViewModelMiss(triggerPath, 'trigger path not found on view model');
        return false;
      }

      if (typeof trigger.trigger === 'function') {
        trigger.trigger();
        return true;
      } else {
        logToNewRelic('Rive View Model Trigger', { message: 'Trigger not found', triggerPath }, 'error');
      }

      return false;
    },
    [rive, reportViewModelMiss],
  );

  const onRiveViewModelTrigger = useCallback(
    (triggerPath: string, callback: () => void): (() => void) => {
      if (!rive) return noop;

      const vm = rive.viewModelInstance;
      if (!vm) {
        reportViewModelMiss(triggerPath, 'no view model instance bound');
        return noop;
      }

      const trigger = vm.trigger?.(triggerPath);
      if (!trigger || typeof trigger.on !== 'function') {
        reportViewModelMiss(triggerPath, 'trigger path not found on view model');
        return noop;
      }

      trigger.on(callback);
      return () => trigger.off(callback);
    },
    [rive, reportViewModelMiss],
  );

  const setRiveViewModelEnum = useCallback(
    (enumPath: string, value: string): boolean => {
      if (!rive) return false;

      const vm = rive.viewModelInstance;
      if (!vm) {
        reportViewModelMiss(enumPath, 'no view model instance bound');
        return false;
      }

      const enumProp = vm.enum?.(enumPath);
      if (!enumProp) {
        reportViewModelMiss(enumPath, 'enum path not found on view model');
        return false;
      }

      enumProp.value = value;
      return true;
    },
    [rive, reportViewModelMiss],
  );

  const setRiveViewModelBoolean = useCallback(
    (booleanPath: string, value: boolean): boolean => {
      if (!rive) return false;

      const vm = rive.viewModelInstance;
      if (!vm) {
        reportViewModelMiss(booleanPath, 'no view model instance bound');
        return false;
      }

      const booleanProp = vm.boolean?.(booleanPath);
      if (!booleanProp) {
        reportViewModelMiss(booleanPath, 'boolean path not found on view model');
        return false;
      }

      booleanProp.value = value;
      return true;
    },
    [rive, reportViewModelMiss],
  );

  return {
    rive,
    isLoading: isTimeoutLoading && !riveFile,
    timeoutError,
    error,
    RiveComponent,
    useStateMachineInput,
    fireRiveViewModelTrigger,
    onRiveViewModelTrigger,
    setRiveViewModelEnum,
    setRiveViewModelBoolean,
  };
};
